import {
  type ButtonHTMLAttributes,
  type CSSProperties,
  forwardRef,
} from "react";

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
}

const variantStyles: Record<ButtonVariant, CSSProperties> = {
  primary: {
    backgroundColor: "var(--cn-horror-p1)",
    color: "var(--cn-horror-finish-stripe-light)",
    border: "1px solid var(--cn-horror-p1)",
    fontWeight: "bold",
    boxShadow: "0 0 8px var(--cn-horror-p1)",
  },
  secondary: {
    backgroundColor: "var(--cn-horror-card-bg)",
    color: "var(--cn-horror-text)",
    border: "1px solid var(--cn-horror-surface-border)",
  },
  ghost: {
    backgroundColor: "transparent",
    color: "var(--cn-horror-text-muted)",
    border: "1px solid var(--cn-horror-surface-border)",
  },
  danger: {
    backgroundColor: "transparent",
    color: "var(--cn-horror-error)",
    border: "1px solid var(--cn-horror-error)",
  },
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "px-3 py-1.5 text-xs",
  md: "px-4 py-2.5 text-sm",
  lg: "px-6 py-3 text-base",
};

// forwardRef so Headless UI <Transition> (and other ref consumers) can attach a
// ref to the underlying <button>. Without this, wrapping <Button> in a
// <Transition> throws "Function components cannot be given refs" (crash on the
// SecretSelection confirm button when 2 characters are picked).
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = "primary",
      size = "md",
      className = "",
      disabled,
      style,
      ...props
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        className={`${sizeClasses[size]} cursor-pointer rounded-md transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
        style={{ ...variantStyles[variant], ...style }}
        {...props}
      />
    );
  },
);
